import React, { ReactElement, useState } from 'react';
import Select from 'react-select';
import { useHistory } from 'react-router-dom';
import { cartState } from 'app-state/cart-state';
import useCartActions from 'app-state/cart-actions';
import { Button } from 'components/common/Button';
import { EmptyCartPage } from '../cart/Cart';
import CartTable from 'components/common/CartTable';
import { FormLabelWrapper, makeFormFieldState, FormField } from 'components/common/form-utils/form';
import { Validators, validateForm, validateFormField } from 'components/common/form-utils/validators';
import { generateOrderNumber } from './Order';
import { FormErrorMessage } from 'components/common/form-utils/form-elements';
import { Voucher } from 'utils/Voucher';
import VoucherApplyField from 'components/common/VoucherApplyField';
import { useRecoilValue } from 'recoil';
import LoadingModal from 'components/common/modals/LoadingModal';
import { progressLoadingBar, getRandomLoadingTime } from 'utils/loading-bar-progress';

const ValidCheckoutPage = (): ReactElement => {
  const history = useHistory();
  const { cartItemCount } = useRecoilValue(cartState);
  const { emptyCart } = useCartActions();
  const [submitLoadingModalOpen, setSubmitLoadingModalOpen] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const form = makeFormFieldState({
    forename: { visibleName: 'Forename', required: true },
    surname: { visibleName: 'Surname', required: true },
    email: { visibleName: 'Email', required: true, validations: [Validators.isEmail()] },
    telephone: {
      visibleName: 'Telephone', required: true, validations: [
        Validators.hasLength(10, { excludeWhitespace: true }), Validators.isNumeric({ allowWhitespace: true })],
    },
    address: { visibleName: 'Address', required: true },
    comments: { visibleName: 'Comments' },
    cardType: { visibleName: 'Card Type', required: true },
    cardNumber: {
      visibleName: 'Card Number', required: true, validations: [
        Validators.hasLength(16, { excludeWhitespace: true }), Validators.isNumeric({ allowWhitespace: true })],
    },
  });

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm(form)) return;
    setSubmitLoadingModalOpen(true);
    await progressLoadingBar({
      loadingTime: getRandomLoadingTime({ min: 10000, max: 30000 }),
      onUpdate: ({ percentage }) => setSubmissionProgress(percentage),
    });
    setSubmitLoadingModalOpen(false);
    setSubmissionProgress(0);
    emptyCart();
    history.push(`/order/${generateOrderNumber()}?name=${form.forename.value}`);
  };

  const [appliedVoucher, setAppliedVoucher] = useState({} as Voucher | undefined);

  return <React.Fragment>
    <div className='py-8 align-middle inline-block min-w-full sm:px-6 lg:px-8'>
      <div id='cart-contents-message' className='pb-8 text-2xl'>
        <strong>Almost there</strong> - your
        {cartItemCount === 1 && <><strong> {cartItemCount} item</strong> is </>}
        {cartItemCount !== 1 && <><strong> {cartItemCount} items</strong> are </>}
        almost in your hands.
      </div>
      <div className='flex items-start place-content-center pb-8 w-auto p-4 md:w-4/5 lg:w-1/2 m-auto'>
        <CartTable readonly showTotal appliedVouchers={
          appliedVoucher && appliedVoucher.isValid ? [appliedVoucher] : []
        } />
      </div>
      <VoucherApplyField onApply={setAppliedVoucher} />

      <div className='m-auto w-3/5'>
        <form onSubmit={onSubmit}>
          <div className='flex flex-col place-items-start'>
            <div className='flex'>
              <h2 className='text-2xl p-2'>Delivery Details</h2>
            </div>

            <FormField field={form.forename} validateOnChange />
            <FormField field={form.surname} validateOnChange />
            <FormField field={form.email} validateOnChange />
            <FormField field={form.telephone} validateOnChange />
            <FormField textarea field={form.address} validateOnChange />
            <FormField textarea field={form.comments} validateOnChange />

            <h2 className='pl-2 pb-2 pt-8 text-2xl'>Payment Details</h2>

            <FormLabelWrapper
              name='Card Type'
              id='card-type-label'
              requiredLabel
              input={<React.Fragment>
                <div className='w-full flex flex-row'>
                  <Select
                    aria-labelledby='card-type-label'
                    id='card-type'
                    className={`w-48 border-2 ${form.cardType.error ? 'border-red-200' : 'border-gray-300'}`}
                    styles={{
                      control: (provided) => ({ ...provided, 'border': 'none' }),
                    }}
                    options={[{ value: 'VISA', label: 'VISA' }, { value: 'MasterCard', label: 'MasterCard' }]}
                    onChange={event => {
                      event && validateFormField(form.cardType, event.value);
                      event && form.cardType.setValue(event.value);
                    }}
                  />
                  <span className='pl-4 flex items-center'>
                    {form.cardType.error && <FormErrorMessage>{form.cardType.error}</FormErrorMessage>}
                  </span>
                </div>
              </React.Fragment>}
            />

            <FormField
              field={form.cardNumber}
              validateOnChange />

            <div className='w-full grid place-items-end'>
              <span className='p-2'>
                <Button primary id='submit-button' type='submit'>Submit</Button>
              </span>
            </div>
          </div>
        </form>
      </div>
    </div>
    <LoadingModal
      isOpen={submitLoadingModalOpen}
      heading='Processing your order'
      progress={submissionProgress}
    />
  </React.Fragment>;
};

const CheckoutPage = (): ReactElement => {
  const { cartItemCount } = useRecoilValue(cartState);
  return (cartItemCount === 0 ? <EmptyCartPage /> : <ValidCheckoutPage />);
};

export default CheckoutPage;
